// The sales lists' one filter row (rev-7 FilterBar, D-445). A screen lists its
// filters once, each with its control, whether it sits in the row, and the
// chip that says it is applied; this decides where each control is drawn,
// which chips show and what the Filters button counts.
//
// The rule the primitive leaves to its caller: a filter in the row shows its
// own value from 769 px up, so it gets no chip there. Below that the row's
// filters move into the sheet with the rest, and every applied filter is a
// chip, so a phone reader can see what narrowed the list without opening it.

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  FilterBar,
  type FilterBarSearch,
  type FilterChip,
} from "../../../../components/filter-bar";
import { useDesktop } from "../../../../components/use-desktop";

export interface SalesFilter {
  readonly key: string;
  /** The filter's own `Field`, with its own test id. */
  readonly control: ReactNode;
  /** In the row (at most two per screen); otherwise behind Filters. */
  readonly inline?: boolean;
  /** "Status: Open" while the filter narrows the list, else undefined. */
  readonly applied?: string | undefined;
  /** Clears this one filter. A filter that is applied but has no `onClear`
   * is counted on the button and gets no chip, for a filter the screen
   * already names in its own line under the bar. */
  readonly onClear?: (() => void) | undefined;
}

export function SalesFilterBar({
  filters,
  search,
  actions,
  onClearAll,
  ...rest
}: {
  readonly filters: readonly SalesFilter[];
  readonly search?: FilterBarSearch | undefined;
  readonly actions?: ReactNode;
  /** Clears every filter at once; offered once two or more are applied. */
  readonly onClearAll?: (() => void) | undefined;
  readonly "data-testid": string;
}) {
  const { t } = useTranslation();
  const desktop = useDesktop();
  const visible = filters.filter((f) => f.control !== null && f.control !== false);
  const inline = visible.filter((f) => f.inline === true);
  const more = visible.filter((f) => f.inline !== true);
  // A chip for every applied filter the reader cannot see in the row.
  const chips: FilterChip[] = visible
    .filter((f) => f.applied !== undefined && f.onClear !== undefined)
    .filter((f) => !desktop || f.inline !== true)
    .map((f) => ({ key: f.key, label: f.applied ?? "", onRemove: f.onClear ?? (() => undefined) }));
  const counted = visible.filter(
    (f) => f.applied !== undefined && (!desktop || f.inline !== true),
  ).length;
  const appliedTotal = visible.filter((f) => f.applied !== undefined).length;
  return (
    <FilterBar
      data-testid={rest["data-testid"]}
      search={search}
      filters={
        visible.length === 0
          ? undefined
          : {
              inline:
                inline.length > 0
                  ? inline.map((f) => <Slot key={f.key}>{f.control}</Slot>)
                  : undefined,
              more:
                more.length > 0 ? more.map((f) => <Slot key={f.key}>{f.control}</Slot>) : undefined,
              label:
                counted === 0
                  ? t("sales.common.filters")
                  : t("sales.common.filtersCount", { count: counted }),
              title: t("sales.common.filtersTitle"),
              doneLabel: t("sales.common.filtersDone"),
            }
      }
      chips={chips}
      removeLabel={(chip) => t("sales.common.filterRemove", { filter: chip.label })}
      clearAll={
        onClearAll !== undefined && appliedTotal > 1
          ? { label: t("sales.common.filtersClear"), onClear: onClearAll }
          : undefined
      }
      actions={actions}
    />
  );
}

function Slot({ children }: { readonly children: ReactNode }) {
  return <>{children}</>;
}
